import { Injectable } from '@angular/core';
import { action, autorun, computed, makeObservable, observable, toJS, when } from 'mobx';
import { StorageService } from 'storage.service';
import { RemoteConfigProps } from '../services/firebase.service.const';
import { RecallResponse } from '../services/recall.service.const';

@Injectable()
export class UserStore {
  public initialState;
  private readonly tableName = 'surgical-fmta';
  private readonly debounce = 500;
  private readonly nonPersistedStateProperties = ['nonPersistedStateProperties', 'initialState', 'logger', 'storageService', 'debounce'];

  @observable private initialized = false;
  @observable public remoteConfig: RemoteConfigProps = null;
  @observable public recall: RecallResponse = null;

  @observable public eulaAcceptedOn: string = null;
  @observable public isEulaAccepted = false;
  @observable public isHCP = false;
  @observable public marketId: ISOCountry = null;
  @observable public locale: ISOLocale = null;
  @observable public authenticated = false;
  @observable public privacyData = null;
  @observable public resources = [];

  constructor(private readonly storageService: StorageService) {
    makeObservable(this);
  }
  @computed public get isAuthenticated() {
    return this.authenticated === true;
  }

  @action
  public async initStore() {
    await this.storageService.initTable(this.tableName);
    this.initialState = toJS(this);
    await this.loadStoreFromDatabase();
    this.persistStoreToDatabase();
    this.initialized = true;
  }

  public async isReady(): Promise<true> {
    return new Promise(resolve => {
      when(
        () => this.initialized === true,
        () => resolve(true)
      );
    });
  }

  @action
  private async loadStoreFromDatabase() {
    const state = await this.storageService.get(this.tableName);
    for (const key of Object.keys(state)) {
      this[key] = state !== null ? state[key] : this[key];
    }
  }

  @action
  public persistStoreToDatabase() {
    autorun(
      () => {
        const store = {};
        for (const key of this.persistedStateKeys) {
          if (this[key] !== undefined) {
            store[key] = toJS(this[key]);
          }
        }
        this.storageService.save(this.tableName, store);
      },
      { delay: this.debounce }
    );
  }

  @action
  public syncState(state: string, modifiedRecord) {
    const existingRecordIndex = this[state]?.findIndex(record => record.id === modifiedRecord.id);
    if (modifiedRecord.isActive === false && existingRecordIndex > -1) {
      // Delete record
      this[state] = this[state].splice(existingRecordIndex, 1);
    } else if (existingRecordIndex > -1) {
      // Update record
      this[state] = [...this[state]];
      this[state][existingRecordIndex] = modifiedRecord;
    } else if (existingRecordIndex === -1 || existingRecordIndex === undefined) {
      // Create record
      this[state] = [...this[state]];
      this[state].push(modifiedRecord);
    }
  }

  @computed
  public get persistedStateKeys() {
    return Object.keys(this.initialState).filter(value => !this.nonPersistedStateProperties.includes(value));
  }

  @action
  public updateUser(userAttributes) {
    for (const key of Object.keys(userAttributes)) {
      this[key] = userAttributes[key];
    }
  }

  @action
  public logout() {
    this.resetStore();
  }

  @action
  public resetStore() {
    for (const key of this.persistedStateKeys) {
      if (this.keepDataFor(key)) continue;
      this[key] = this.initialState[key];
    }
    this.initialized = true;
  }

  private keepDataFor(key: string): boolean {
    return key === 'eulaAcceptedOn' || key === 'marketId' || key === 'locale';
  }

  @computed
  public get activeLogs() {
    return true;
  }
}
